import { debounce, isEqual } from "lodash"
import type { ReactElement, ReactNode } from "react"
import React from "react"

import type { Background } from "@/lib/background"
import clsxm from "@/lib/clsxm"
import type { Geolocation } from "@/lib/geolocation"
import { Subscription } from "@/lib/subscription"

import { BookingSearchDropdown } from "@/components/Booking/BookingSearchDropdown"
import type { BookingSearchDropdownProps } from "@/components/Booking/BookingSearchDropdown/component"
import Icon from "@/components/common/Icon/Icon"
import TextField from "@/components/common/Textfield/TextField"

import { CloseIcon } from "@/constants/icons"

export type BookingSearchAddressInputProps = {
  background: Background.ContextData
  children?: ReactNode
  className?: React.HTMLAttributes<HTMLDivElement>[`className`]
  dropdownAlignOffset: number
  dropdownWidth: number
  geolocation: Geolocation.ContextData
  htmlForSearch?: string
  onLocationResolve?: BookingSearchDropdownProps[`onLocationResolve`]
  onSearchChange: (searchValue: string) => void
  searchValue?: string
  syncWidth?: (args: { width: number }) => void
}

type State = {
  dropdownVisible: boolean
  textInput: string
  width: number
}

export class BookingSearchAddressInputComponent extends React.Component<
  BookingSearchAddressInputProps,
  State
> {
  private containerRef?: HTMLDivElement
  private dropdownMenuListenerId = `BookingSearchAddressInputDropdownMenu`
  private textInputRef?: HTMLInputElement
  private unsubscribers: Subscription.Unsubscribers = {}

  public state = {
    dropdownVisible: false,
    textInput: this.props.searchValue || ``,
    width: 0,
  }

  public componentDidMount = () => {
    this.syncContainerWidth()
  }

  public componentDidUpdate = (
    _: BookingSearchAddressInputProps,
    prevState: State
  ) => {
    this.syncContainerWidth()
    this.syncDropdownVisibility(prevState)
  }

  public componentWillUnmount = () => {
    Subscription.cancelAll(this.unsubscribers)
    this.syncContainerWidth.cancel()
  }

  private syncDropdownVisibility = (prevState: State) => {
    if (!this.state.dropdownVisible && this.props.geolocation.processing) {
      this.setState({ dropdownVisible: true })
      return
    }

    if (this.state.dropdownVisible !== prevState.dropdownVisible) {
      this.onDropdownVisibilityChange(this.state)
    }
  }

  private onBackgroundClick: Background.Functions.Handler = () => {
    this.setState({ dropdownVisible: false })
  }

  private onTextFieldClick: React.MouseEventHandler<HTMLDivElement> = (
    event
  ) => {
    event.preventDefault()
    event.stopPropagation()

    this.setState({
      dropdownVisible: !this.state.dropdownVisible,
    })
  }

  private onClearClick: React.MouseEventHandler<HTMLDivElement> = () => {
    this.onTextChange({ textInput: `` })
  }

  private onDropdownVisibilityChange = ({
    dropdownVisible,
  }: Pick<State, `dropdownVisible`>) => {
    if (dropdownVisible) {
      document.body.style.pointerEvents = ``

      this.unsubscribers[this.dropdownMenuListenerId] =
        this.props.background.subscribeToBackgroundClicks({
          listenerId: `BookingSearchAddressInput`,
          onBackgroundClick: this.onBackgroundClick,
        })
    } else {
      this.unsubscribers = Subscription.cancel(
        this.unsubscribers,
        this.dropdownMenuListenerId
      )
    }
  }

  private onLocationResolve: BookingSearchDropdownProps[`onLocationResolve`] =
    ({ address, position }) => {
      this.props.onSearchChange(address.formattedAddress)
      this.setState({
        dropdownVisible: false,
        textInput: address.formattedAddress,
      })
      this.textInputRef?.focus()
      this.props.onLocationResolve?.({ address, position })
    }

  private onTextFieldChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    this.onTextChange({ textInput: event.target.value })
  }

  private onTextChange = ({ textInput }: Pick<State, `textInput`>) => {
    this.props.onSearchChange(textInput)
    this.setState({ textInput, dropdownVisible: textInput.length > 0 })
    this.textInputRef?.focus()
  }

  public render = (): ReactElement => {
    return (
      <div
        className={clsxm(`flex flex-row items-center`, this.props.className)}
        ref={(containerRef) => {
          if (containerRef) {
            this.containerRef = containerRef
          }
        }}
      >
        <TextField
          autoFocus
          className={`pointer-events-auto text-base`}
          id={this.props.htmlForSearch || `address`}
          label={`Search by address (e.g. 123 Main Road)`}
          onChange={this.onTextFieldChange}
          onClick={this.onTextFieldClick}
          placeholder={`Search by address (e.g. 123 Main Road)`}
          ref={(textInputRef) => {
            if (textInputRef) {
              this.textInputRef = textInputRef
            }
          }}
          type={`text`}
          value={this.state.textInput}
          variant={`minimal`}
          wrapperClassName={`flex-1`}
        >
          <BookingSearchDropdown
            contentProps={{
              alignOffset: this.props.dropdownAlignOffset,
              sideOffset: 14, // Prevents the dropdown from appearing inside the address input,
              style: { width: this.props.dropdownWidth },
            }}
            onLocationResolve={this.onLocationResolve}
            open={this.state.dropdownVisible}
            textInput={this.state.textInput}
          >
            <div
              className={
                this.state.textInput.length === 0
                  ? `invisible w-0`
                  : `cursor-pointer`
              }
              onClick={this.onClearClick}
            >
              <Icon icon={CloseIcon} className={`pr-2`} />
            </div>
          </BookingSearchDropdown>
        </TextField>
        {this.props.children}
      </div>
    )
  }

  public shouldComponentUpdate = (
    nextProps: Readonly<BookingSearchAddressInputProps>,
    nextState: Readonly<State>
  ) => {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState)
  }

  private syncContainerWidth = debounce(
    () => {
      if (this.containerRef) {
        const { width } = this.containerRef.getBoundingClientRect()
        if (width !== this.state.width) {
          this.setState({ width })
          this.props.syncWidth?.({ width })
        }
      }
    },
    350,
    { leading: true, trailing: true }
  )
}
